import { useState } from "react";
import { MapPin } from "lucide-react";
import { Button, Card } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { currentPosition } from "../../lib/geo";
import { useAuth } from "../../hooks/useAuth";

interface NearbyJob {
  id: string;
  title: string;
  description: string;
  address: string;
  distanceMi: number;
  payCents: number | null;
}

export function JobsNearMe() {
  const { tenantId } = useAuth();
  const [jobs, setJobs] = useState<NearbyJob[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function search() {
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      const geo = await currentPosition();
      if (!geo) {
        setMessage("Couldn't get your location — allow location access and try again.");
        return;
      }
      const res = await call<{ jobs: NearbyJob[] }>(
        "listNearbyOpenJobs",
        // 40 miles; the server API speaks km internally.
        { tenantId, lat: geo.lat, lng: geo.lng, radiusKm: 64 },
        { mutation: false },
      );
      setJobs(res.jobs);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function claim(openJobId: string) {
    if (!tenantId) return;
    setMessage("");
    try {
      await call("claimOpenJob", { tenantId, openJobId });
      setJobs((prev) => prev?.filter((j) => j.id !== openJobId) ?? null);
      setMessage("Job is yours. It's on your board.");
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-bold">Jobs Near Me</h1>
      <Card className="text-center">
        <Button onClick={search} busy={busy} className="w-full py-3">
          <MapPin size={16} /> Find posted jobs near my location
        </Button>
        {message ? <p className="mt-2 text-xs text-ink">{message}</p> : null}
      </Card>
      {jobs !== null ? (
        <Card className="p-0">
          {jobs.map((j) => (
            <div key={j.id} className="flex items-center justify-between gap-3 border-b border-steel/10 px-4 py-3 last:border-0">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-ink">{j.title}</div>
                <div className="truncate text-xs text-steel">
                  {j.distanceMi} mi away{j.address ? ` · ${j.address}` : ""}
                </div>
                {j.payCents !== null ? (
                  <div className="text-xs font-medium text-success">${(j.payCents / 100).toFixed(2)}</div>
                ) : null}
              </div>
              <Button className="shrink-0 px-3 py-1 text-xs" onClick={() => void claim(j.id)}>
                Pick up
              </Button>
            </div>
          ))}
          {jobs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-steel">Nothing posted within 40 miles right now.</div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
